import { ReactNode, useId } from 'react';
import ReactModal from 'react-modal';
import { FiX } from 'react-icons/fi';

import { useAppInerte } from 'utils/app-inerte';
import { FOCO_VISIVEL } from 'utils/foco';

/**
 * - `sm`: cartão de até 440px, para avisos e confirmações.
 * - `md`: cartão de até 720px com a altura do conteúdo (tutorial, preferências).
 * - `lg`: cartão de até 1120px com a altura da tela, para texto longo com rolagem própria (regulamento).
 */
export type ModalFrameSize = 'sm' | 'md' | 'lg';

/**
 * - `cabecalho`: cabeçalho azul com título amarelo (modal de regulamento do `Home.dc.html`).
 * - `cartao`: cartão branco com borda rosa e título azul-escuro, sem faixa de cabeçalho (modais de ação de
 *   `Realizacao Destaque.dc.html` e `Minha Equipe.dc.html`).
 */
export type ModalFrameAppearance = 'cabecalho' | 'cartao';

interface ModalFrameProps {
  isOpen: boolean;
  title: string;
  /** Linha sob o título. Vira a descrição acessível do diálogo. */
  subtitle?: ReactNode;
  /** Sem subtítulo, o `id` do elemento do corpo que descreve o diálogo (o texto de um aviso). */
  descriptionId?: string;
  /** Conteúdo do cabeçalho sob a linha do título (as etapas de um fluxo). */
  headerExtra?: ReactNode;
  /** `alertdialog` para avisos que interrompem e pedem resposta. Padrão `dialog`. */
  role?: 'dialog' | 'alertdialog';
  /** Pílula amarela à direita do cabeçalho. */
  badge?: string;
  /**
   * Sem ela o modal só fecha pelas ações do rodapé: nem botão de fechar, nem Esc.
   * O clique fora nunca fecha.
   */
  onClose?: () => void;
  /** Nome acessível do botão de fechar. */
  closeLabel?: string;
  footer?: ReactNode;
  size?: ModalFrameSize;
  /** Padrão `cabecalho`. */
  appearance?: ModalFrameAppearance;
  /** Só em `cartao`: imagem sobre o título, com o cabeçalho e o rodapé centralizados (tela de resultado). */
  illustration?: ReactNode;
  children: ReactNode;
}

/**
 * O que o modal esconde do leitor de tela (`aria-hidden`) enquanto está aberto: a aplicação
 * inteira, em `#root`. O `setAppElement('#modal')` usado no resto do repo esconde uma div vazia,
 * e o VoiceOver saía do diálogo para ler a página por trás (o Chrome não poda a árvore só pelo
 * `aria-modal`). Vai por instância, e não pelo setter global, para não mudar os outros modais.
 */
const APP_ROOT_ID = 'root';

// D3: max-w não tem escala de spacing no Tailwind 3.3, e a altura máxima de 940px do lg não
// tem degrau.
const CARD_SIZE: Record<ModalFrameSize, string> = {
  sm: 'max-w-[440px] max-h-full',
  md: 'max-w-[720px] max-h-full',
  lg: 'max-w-[1120px] h-full lg:h-[min(100%,940px)]',
};

const CARD_BORDER: Record<ModalFrameAppearance, string> = {
  cabecalho: '',
  cartao: 'border-4 border-solid border-brand-pink-light',
};

/**
 * Moldura dos modais da nova identidade, a partir do modal de regulamento do `Home.dc.html`:
 * fundo azul-marinho translúcido, cartão branco de raio 24px, cabeçalho azul com título
 * amarelo e rodapé separado por borda. O corpo é livre: quem usa cuida do padding e da rolagem.
 *
 * Duas diferenças do design, por contraste (WCAG 1.4.3): o subtítulo vai em branco pleno (a 92%
 * ficava em 4,38:1) e o selo vai sobre `brand-blue-dark` (amarelo sobre o branco translúcido
 * dava 3,48:1; agora 8,15:1).
 */
export function ModalFrame({
  isOpen,
  title,
  subtitle,
  descriptionId,
  headerExtra,
  role = 'dialog',
  badge,
  onClose,
  closeLabel = 'Fechar',
  footer,
  size = 'md',
  appearance = 'cabecalho',
  illustration,
  children,
}: ModalFrameProps) {
  const titleId = useId();
  const subtitleId = useId();

  useAppInerte(isOpen);

  const cartao = appearance === 'cartao';

  return (
    <ReactModal
      isOpen={isOpen}
      appElement={document.getElementById(APP_ROOT_ID) ?? undefined}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={!!onClose}
      role={role}
      aria={{ labelledby: titleId, describedby: subtitle ? subtitleId : descriptionId }}
      portalClassName="relative z-50"
      // D3: 30px não tem degrau no Tailwind.
      overlayClassName="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(0,45,75,0.62)] p-4 lg:p-[30px]"
      className={`relative flex w-full flex-col overflow-hidden rounded-jj-2xl bg-brand-white shadow-jj-modal-brand outline-none animate-jj-pop motion-reduce:animate-none ${CARD_BORDER[appearance]} ${CARD_SIZE[size]}`}
    >
      {cartao ? (
        <CartaoHeader
          titleId={titleId}
          title={title}
          subtitleId={subtitleId}
          subtitle={subtitle}
          illustration={illustration}
          onClose={onClose}
          closeLabel={closeLabel}
        />
      ) : (
        // `div`, e não `header`: fora de uma seção, o `header` vira o marco `banner` (o cabeçalho do
        // site), e dois modais empilhados passavam a ter dois.
        // D3: 18px e 22px não têm degrau no Tailwind.
        <div data-modal-header className="shrink-0 bg-brand-blue px-5 py-[18px] text-brand-white lg:px-7 lg:py-[22px]">
          <div className="flex items-center gap-4">
            <div className="min-w-0 flex-1">
              <h2
                id={titleId}
                className="m-0 font-display text-xl font-extrabold leading-jj-display text-brand-yellow lg:text-2xl"
              >
                {title}
              </h2>
              {subtitle && (
                <p id={subtitleId} className="m-0 mt-1.5 text-jj-body-sm">
                  {subtitle}
                </p>
              )}
            </div>

            {badge && (
              <span className="shrink-0 rounded-jj-pill bg-brand-blue-dark px-4 py-2 font-display text-xs font-bold text-brand-yellow">
                {badge}
              </span>
            )}

            {/* D3: 34px não tem degrau no Tailwind. */}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label={closeLabel}
                className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-brand-white text-brand-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-yellow"
              >
                <FiX size={17} strokeWidth={2.8} aria-hidden="true" />
              </button>
            )}
          </div>

          {headerExtra}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{children}</div>

      {/* D3: 18px, 22px, 26px e 30px não têm degrau no Tailwind. */}
      {footer && (
        <footer
          className={
            cartao
              ? `flex shrink-0 flex-wrap items-center gap-2.5 px-5 pb-[26px] pt-[22px] lg:px-[30px] ${illustration ? 'justify-center' : ''}`
              : 'flex shrink-0 flex-wrap items-center gap-[18px] border-t border-surface-200 px-5 pb-5 pt-[18px] lg:px-7'
          }
        >
          {footer}
        </footer>
      )}
    </ReactModal>
  );
}

interface CartaoHeaderProps {
  titleId: string;
  title: string;
  subtitleId: string;
  subtitle?: ReactNode;
  illustration?: ReactNode;
  onClose?: () => void;
  closeLabel: string;
}

/**
 * Cabeçalho da aparência `cartao` (design dos modais de ação): título azul-escuro sobre o branco, subtítulo em
 * cinza e o fechar redondo azul com o X amarelo no canto. Com ilustração, vira o topo da tela de resultado
 * ("Mandou bem!"): imagem, título grande e tudo centralizado.
 */
function CartaoHeader({ titleId, title, subtitleId, subtitle, illustration, onClose, closeLabel }: CartaoHeaderProps) {
  return (
    // D3: 26px, 28px e 30px não têm degrau no Tailwind; o recuo à direita abre espaço para o fechar.
    <div
      data-modal-header
      className={`shrink-0 px-5 pt-7 lg:px-[30px] ${illustration ? 'flex flex-col items-center gap-3.5 text-center' : 'pr-14 lg:pr-16'}`}
    >
      {illustration}
      <h2
        id={titleId}
        className={`m-0 font-display text-brand-blue-dark ${illustration ? 'text-[26px] font-extrabold leading-tight' : 'text-[19px] font-bold leading-snug'}`}
      >
        {title}
      </h2>
      {subtitle && (
        <p id={subtitleId} className="m-0 mt-1.5 text-[12.5px] leading-[1.6] text-content-soft">
          {subtitle}
        </p>
      )}

      {/* D3: 34px não tem degrau no Tailwind. */}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className={`absolute right-4 top-3.5 flex h-[34px] w-[34px] items-center justify-center rounded-full bg-brand-blue text-brand-yellow ${FOCO_VISIVEL}`}
        >
          <FiX size={17} strokeWidth={2.8} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
